import type { Module, ModuleFolderStructureItem, TemplateInfo } from "@scayla/api-client-fetch";

import { filterNavTreeForFavorites } from "@/components/navigation/favorites";
import type { useLayoutStore } from "@/store/layoutStore";
import type { NavNode } from "@/utils/navUtils";

export type FavoriteItem =
  | { type: "template"; data: TemplateInfo }
  | { type: "folder"; data: ModuleFolderStructureItem }
  | { type: "node"; data: NavNode };

export function filterModuleForFavorites(
  module: Module,
  layoutStore: ReturnType<typeof useLayoutStore>,
  getModuleKey: (id: string) => string,
  getModuleTopLevelItems: (id: string) => FavoriteItem[],
): (Module & { filteredItems: FavoriteItem[]; isFavorite: boolean }) | null {
  const isFavorite = layoutStore.isFavorite(getModuleKey(module.id));
  const items = getModuleTopLevelItems(module.id);

  let filteredItems: FavoriteItem[] = [];

  if (isFavorite) {
    // Show all children if module is favorite
    filteredItems = items;
  } else if (items && items.length > 0) {
    filteredItems = items
      .map((item): FavoriteItem | null => {
        if (item.type === "template") {
          const isTemplateFavorite = layoutStore.isFavorite(item.data.id ?? "");
          return isTemplateFavorite ? item : null;
        } else if (item.type === "folder") {
          const filteredFolder = filterFolderForFavorites(item.data, layoutStore);
          return filteredFolder ? { ...item, data: filteredFolder } : null;
        } else if (item.type === "node") {
          const filteredNode = filterNavTreeForFavorites(item.data, layoutStore);
          return filteredNode ? { ...item, data: filteredNode } : null;
        }
        return null;
      })
      .filter((item): item is FavoriteItem => item !== null);
  }

  if (isFavorite || filteredItems.length > 0) {
    return {
      ...module,
      filteredItems,
      isFavorite,
    };
  }

  return null;
}

function filterFolderForFavorites(
  folder: ModuleFolderStructureItem,
  layoutStore: ReturnType<typeof useLayoutStore>,
): ModuleFolderStructureItem | null {
  // Filter templates: only keep favorite templates
  const filteredTemplates = folder.templates?.filter((template) => layoutStore.isFavorite(template.id ?? "")) ?? [];

  // Filter subfolders recursively: only keep if they have favorite templates inside
  const filteredFolders =
    folder.folders?.map((sub) => filterFolderForFavorites(sub, layoutStore)).filter((sub) => sub !== null) ?? [];

  const isFolderFavorite = layoutStore.isFavorite(folder.id);

  // Only return folder if it is favorite itself OR has favorite templates OR subfolders with favorites
  if (isFolderFavorite || filteredTemplates.length > 0 || filteredFolders.length > 0) {
    return {
      ...folder,
      templates: filteredTemplates.length > 0 ? filteredTemplates : undefined,
      folders: filteredFolders.length > 0 ? filteredFolders : undefined,
    };
  }

  return null;
}
